<template>
  <div v-if="hasAccess" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card border-0" style="height: calc(100vh - 120px); display: flex; flex-direction: column;">
      <div
        class="card-header bg-white py-3 d-flex flex-wrap align-items-center justify-content-between gap-3 border-bottom">
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0 fw-bold">Client List</h5>
        </div>
        <div class="d-flex flex-wrap gap-2 flex-grow-1 justify-content-center">
          <IconField class="search-field" style="width: 100%; max-width: 500px;">
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
v-model="table.search" type="text" size="small" placeholder="Search Client" class="rounded-pill"
              style="width: 100%;" @keyup="filter" />
          </IconField>
        </div>
        <div>
          <button type="button" class="mx-1 mb-0 btn btn-sm btn-primary" @click="openAddModal">Add</button>
        </div>
      </div>
      <div style="flex: 1; overflow-y: auto;">
        <DataTable
:value="table.onLoad ? dummyRows : table.data" removable-sort scrollable scroll-height="flex"
          table-style="min-width: 100%; width: max-content;">
          <Column field="code" header="Code" header-class="bg-light text-nowrap text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.code ? slotProps.data.code : '' }}
            </template>
          </Column>
          <Column field="description" header="Description" header-class="bg-light text-nowrap text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.description ? slotProps.data.description : '' }}
            </template>
          </Column>
          <Column field="created_at" header="Created At" header-class="bg-light text-nowrap text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton />
            </template>
            <template v-else #body="slotProps">
              <ButtonPrime
v-tooltip.bottom="'Update'" icon="pi pi-pencil" outlined rounded size="small"
                class="me-2" @click="openEditModal(slotProps.data)" />
              <ButtonPrime
v-tooltip.bottom="'Delete'" icon="pi pi-trash" outlined rounded severity="danger"
                size="small" @click="deleteItem(slotProps.data.id)" />
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">
                No Data
              </div>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination
v-model="table.page.current_pages" :page-count="table.page?.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_pages - 1) * table.page.per_pages + table.data.length }} of {{ table.total }}
        </p>
      </div>
    </div>
    <DialogPrime v-model:visible="modal.visible" modal style="width: 30rem; margin: 10px;" class="client-modal">
      <template #header>
        <div class="d-flex align-items-center gap-3">
          <div>
            <h5 class="mb-0 fw-bold text-dark">{{ modal.title }}</h5>
          </div>
        </div>
      </template>
      <div class="row g-4">
        <div class="col-12">
          <div class="row g-3">
            <div class="col-12">
              <label for="code" class="form-label fw-semibold">Code <span class="text-danger">*</span></label>
              <InputText
id="code" v-model="modal.data.code"
                placeholder="e.g. CLI001"
                :class="`${(modal.errorMessage['code'] && modal.data.code == '') ? 'border-danger' : ''}`" :disabled="modal.editId !== null" fluid />
              <input v-if="modal.editId !== null" type="hidden" name="code" :value="modal.data.code" />
              <small v-if="modal.errorMessage['code'] && modal.data.code == ''" class="text-danger">{{
                modal.errorMessage['code'] }}</small>
            </div>
            <div class="col-12">
              <label for="description" class="form-label fw-semibold">Description <span
                  class="text-danger">*</span></label>
              <InputText
id="description" v-model="modal.data.description"
                placeholder="Client Display Name"
                :class="`${(modal.errorMessage['description'] && modal.data.description == '') ? 'border-danger' : ''}`" fluid />
              <small v-if="modal.errorMessage['description'] && modal.data.description == ''" class="text-danger">{{
                modal.errorMessage['description'] }}</small>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime
type="button" label="Cancel" severity="danger" raised size="small" class="px-4 border-0"
            @click="closeModal()" />
          <ButtonPrime
type="button" severity="contrast" raised size="small" :disabled="modal.onLoading" label="Save Client"
            class="px-4" @click="save()">
            <template v-if="modal.onLoading" #icon>
              <i class="fas fa-spinner fa-spin me-2"></i>
            </template>
          </ButtonPrime>
        </div>
      </template>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import NoAccess from "@/components/NoAccess.vue";
import BasePagination from "@/components/BasePagination.vue";
import moment from "moment";
import api from "@/helpers/api";
import clientService from "@/services/generalMasterData/clientService";

export default {
  components: {
    BasePagination,
    NoAccess,
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    hasAccess() {
      return this.authUserPermissions.includes('client-view');
    },
  },
  data() {
    return {
      table: {
        onLoad: true,
        search: this.$route.query.search || "",
        total: 0,
        data: [],
        limit: 100,
        page: {
          current_pages: 1,
          per_pages: 5,
          total_pages: 1,
        },
        loadTimeout: null,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      modal: {
        visible: false,
        data: { code: '', description: '' },
        errorMessage: {},
        onLoading: false,
        title: '',
        action: '', // 'create' atau 'update'
        editDataId: null,
      },
    };
  },
  mounted() {
    if (this.hasAccess) {
      this.get();
    }
  },
  methods: {
    async get() {
      this.table.onLoad = true;

      try {
        const response = await api(
          this,
          clientService.list({
            search: this.table.search,
            limit: this.table.limit,
            page: this.table.page.current_pages,
          }),
          false
        );

        const paged = response.data || {};
        this.table.data = paged.data || [];
        this.table.total = paged.total || this.table.data.length;
        this.table.page.total_pages = paged.last_page || 1;
        this.table.page.current_pages = paged.current_page || 1;
        this.table.page.per_pages = paged.per_page || this.table.limit;
      } catch (error) {
        this.table.data = [];
        this.table.total = 0;
        this.table.page.total_pages = 1;
      } finally {
        this.table.onLoad = false;
      }
    },
    filter() {
      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        this.$router.push({ query: this.table.search ? { search: this.table.search } : null });
        this.table.page.current_pages = 1;
        this.get();
      }, 500);
    },
    changePage() {
      this.get();
    },
    formatDate(date) {
      return date ? moment(date).format("DD MMMM YYYY, HH:mm") : "-";
    },
    openAddModal() {
      this.modal.visible = true;
      this.modal.data = { code: '', description: '' };
      this.modal.errorMessage = {};
      this.modal.title = 'Add Client';
      this.modal.action = 'create';
      this.modal.editDataId = null;
      this.modal.onLoading = false;
    },
    openEditModal(data) {
      this.modal.visible = true;
      this.modal.data = {
        code: data.code || '',
        description: data.description || ''
      };
      this.modal.errorMessage = {};
      this.modal.title = 'Edit Client';
      this.modal.action = 'update';
      this.modal.editDataId = data.id;
      this.modal.onLoading = false;
    },
    async save() {
      this.modal.errorMessage = {};
      if (!this.modal.data.code) this.modal.errorMessage['code'] = 'Code wajib diisi';
      if (!this.modal.data.description) this.modal.errorMessage['description'] = 'Description wajib diisi';
      if (Object.keys(this.modal.errorMessage).length > 0) return;

      this.modal.onLoading = true;
      try {
        if (this.modal.action === 'update' && this.modal.editDataId) {
          await api(this, clientService.patch(this.modal.editDataId, this.modal.data), true);
        } else {
          await api(this, clientService.create(this.modal.data), true);
        }

        this.modal.visible = false;
        this.get(); // refresh table
      } catch (error) {
        if (error.response?.data?.code == 400 && error.response.data.data) {
          error.response.data.data.forEach((v) => {
            this.modal.errorMessage[v.path] = v.msg;
          });
        } else {
          console.error('Error saving client:', error);
        }
      } finally {
        this.modal.onLoading = false;
        this.modal.action = '';
        this.modal.editDataId = null;
      }
    },
    closeModal() {
      this.modal.visible = false;
      this.modal.data = { code: '', description: '' };
      this.modal.errorMessage = {};
      this.modal.action = '';
      this.modal.editDataId = null;
      this.modal.onLoading = false;
    },
    async deleteItem(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, clientService.delete(id)).then(() => {
            this.get();
          });
        }
      });
    },
  },
};
</script>
